import { useEffect, useRef, useState } from 'react';
import { Sigma, Dices, Play, Pause, ChevronDown, Info, Check, ZapOff, Calculator, Lock, Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useToleranceStore } from '../../stores/toleranceStore';
import { useAuthStore, selectIsPro, selectIsFree, selectPlanStatus } from '../../stores/authStore';
import { toast } from '../../lib/toast';
import { trackEvent } from '../../lib/analytics';
import PortalPopover from './PortalPopover';
import ThermalPicker from './ThermalPicker';
import WissenHinweis from './WissenHinweis';

// Constants — same thresholds as the prototype (app.jsx:27-30). The
// store enforces the 100 / 500_000 hard floor and ceiling for the
// Free + Pro plans respectively; this picker only proposes presets
// the store will accept, plus a custom input for in-between values.
const SAMPLES_PRESETS = [1000, 10000, 50000, 100000, 250000, 500000];
const SAMPLES_WARN_THRESHOLD = 50000;
const SAMPLES_MIN = 100;
const SAMPLES_MAX = 500_000;

const METHODS = [
  { id: 'worstcase',   label: 'Worst Case',   icon: Sigma },
  { id: 'rss',         label: 'RSS',          icon: Sigma },
  { id: 'montecarlo',  label: 'Monte Carlo',  icon: Dices },
];

// ─── Method tabs ───────────────────────────────────────────────────

function MethodTabs({ value, onChange, isFree }) {
  const navigate = useNavigate();
  return (
    <div
      role="tablist"
      aria-label="Berechnungsmodus"
      data-tour="method-switch"
      className="inline-flex bg-surface-2 border border-line"
      style={{ borderRadius: 'var(--radius-md)', padding: 2, height: 32 }}
    >
      {METHODS.map((m) => {
        const Icon = m.icon;
        const active = value === m.id;
        // Monte-Carlo is a Pro-only method per the gating decision
        // logged in the user-feedback memo. Free users see the tab
        // with a lock; clicking it deep-links to billing instead of
        // letting them toggle the mode.
        const locked = isFree && m.id === 'montecarlo';
        return (
          <button
            key={m.id}
            role="tab"
            aria-selected={active}
            onClick={() => {
              if (locked) { navigate('/settings/billing'); return; }
              onChange(m.id);
            }}
            title={locked ? 'Monte-Carlo ist im Pro-Plan verfügbar' : undefined}
            className={`inline-flex items-center gap-1.5 px-3 text-xs transition-colors ${
              active
                ? 'bg-surface text-brand font-semibold shadow-sm'
                : locked
                  ? 'text-ink-4 hover:text-warn font-medium'
                  : 'text-ink-3 hover:text-ink-2 font-medium'
            }`}
            style={{ height: 26, borderRadius: 6, border: 'none', cursor: 'pointer' }}
          >
            <Icon size={12} aria-hidden="true" />
            {m.label}
            {locked && <Lock size={10} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

// ─── Live / Manuell switch ─────────────────────────────────────────
// Compact 2-segment toggle; mirrors prototype `LiveCalcSwitch`
// (handoff app.jsx:444-479). Tooltip explains the trade-off so users
// don't need docs.

function LiveCalcSwitch({ value, onChange, disabled }) {
  const opts = [
    { id: true,  label: 'Live',    icon: Play },
    { id: false, label: 'Manuell', icon: Pause },
  ];
  return (
    <div
      role="group"
      aria-label="Live-Berechnung"
      title={disabled
        ? 'Live-Berechnung ist im Pro-Plan verfügbar.'
        : 'Live-Berechnung: jede Änderung aktualisiert sofort. Bei hohen Monte-Carlo-Samples kann das spürbar langsam werden.'}
      className="inline-flex bg-surface-2 border border-line"
      style={{ borderRadius: 'var(--radius-md)', padding: 2, height: 30, opacity: disabled ? 0.55 : 1 }}
    >
      {opts.map((o) => {
        const Icon = o.icon;
        const active = value === o.id;
        return (
          <button
            key={String(o.id)}
            onClick={() => !disabled && onChange(o.id)}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 text-[11px] transition-colors ${
              active
                ? 'bg-surface text-ink font-semibold shadow-sm'
                : 'text-ink-3 font-medium'
            }`}
            style={{
              height: 24,
              padding: '0 9px',
              borderRadius: 6,
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Icon size={11} aria-hidden="true" />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Samples picker (MC only) ───────────────────────────────────────
// Replaces the prior <select> for Monte-Carlo sample size. Presets
// as chips + custom numeric input + Live-warning when the chosen
// count would slow Live-Berechnung noticeably.

function SamplesPicker({ value, onChange, warn, onDisableLive }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const anchorRef = useRef(null);
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commitCustom = () => {
    const n = parseInt(draft.replace(/\D/g, ''), 10);
    if (!Number.isFinite(n) || n < SAMPLES_MIN) { setDraft(String(value)); return; }
    onChange(Math.min(n, SAMPLES_MAX));
  };

  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 px-2.5 text-xs font-semibold ${
          warn
            ? 'text-warn border-warn/40 bg-warn/10'
            : 'text-ink-2 border-line bg-surface-2'
        }`}
        style={{ height: 30, border: '1px solid', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
      >
        <span className="text-[10.5px] font-bold tracking-wider uppercase text-ink-3">
          Samples
        </span>
        <span className="num">{value.toLocaleString('de-DE')}</span>
        {warn && <Info size={12} aria-hidden="true" />}
        <ChevronDown size={11} aria-hidden="true" />
      </button>

      <PortalPopover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} minWidth={280}>
        <div className="p-2 flex flex-col gap-2">
          <div className="text-[10px] font-bold tracking-wider uppercase text-ink-3 px-0.5">
            Sample-Zahl
          </div>
          <div className="grid grid-cols-3 gap-1">
            {SAMPLES_PRESETS.map((p) => {
              const active = value === p;
              const heavy = p >= SAMPLES_WARN_THRESHOLD;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => { onChange(p); setOpen(false); }}
                  className={`num text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                    active
                      ? 'bg-brand-soft text-brand'
                      : 'text-ink-2 hover:bg-surface-2'
                  }`}
                  style={{
                    padding: '7px 6px',
                    border: '1px solid',
                    borderColor: active ? 'var(--color-brand)' : 'var(--color-line)',
                    borderRadius: 6, cursor: 'pointer',
                  }}
                >
                  {p.toLocaleString('de-DE')}
                  {heavy && <span className="text-warn text-[10px]" aria-hidden="true">⚠</span>}
                </button>
              );
            })}
          </div>

          {/* Custom input — 100–500.000. Commit on Enter or Blur. */}
          <div
            className="flex items-center gap-1.5 pt-1.5"
            style={{ borderTop: '1px dashed var(--color-line)' }}
          >
            <label className="text-[10.5px] font-semibold text-ink-3" htmlFor="samples-custom">
              Eigener Wert
            </label>
            <input
              id="samples-custom"
              inputMode="numeric"
              className="num"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitCustom(); setOpen(false); } }}
              onBlur={commitCustom}
              placeholder={`${SAMPLES_MIN}…${SAMPLES_MAX.toLocaleString('de-DE')}`}
              style={{
                flex: 1, height: 26, padding: '0 8px',
                background: 'var(--color-surface-2)',
                border: '1px solid var(--color-line)',
                borderRadius: 6,
                fontSize: 12, fontWeight: 600,
                color: 'var(--color-ink)',
                outline: 'none',
              }}
            />
          </div>
          <div className="text-[10px] text-ink-4 px-0.5">
            {SAMPLES_MIN.toLocaleString('de-DE')} bis {SAMPLES_MAX.toLocaleString('de-DE')} · mehr Samples = präziser, langsamer
          </div>

          {warn && (
            <div
              role="alert"
              className="flex gap-2 items-start"
              style={{
                padding: '8px 10px',
                background: 'color-mix(in srgb, var(--color-warn) 12%, var(--color-surface))',
                border: '1px solid color-mix(in srgb, var(--color-warn) 35%, var(--color-line))',
                borderRadius: 6,
              }}
            >
              <Info size={13} className="text-warn shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-[11px] text-ink-2 leading-snug">
                Bei dieser Sample-Zahl kann die Live-Berechnung spürbar träge werden.
                <button
                  type="button"
                  onClick={() => { onDisableLive(); setOpen(false); }}
                  className="mt-1.5 inline-flex items-center gap-1 bg-brand text-white text-[11px] font-semibold"
                  style={{ padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer' }}
                >
                  <ZapOff size={11} aria-hidden="true" />
                  Live-Berechnung deaktivieren
                </button>
              </div>
            </div>
          )}
        </div>
      </PortalPopover>
    </div>
  );
}

// ─── Seed Picker (MC only, optional) ──────────────────────────────
// Inline input next to SamplesPicker. Empty = unseeded (Math.random
// per run), number = deterministic. Mirrors the seed control in
// /settings/montecarlo but lives here too so the user can flip
// "reproduzierbar an/aus" without leaving the calculator.
function SeedPicker({ value, onChange }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(value == null ? '' : String(value));
  }

  const commit = () => {
    const trim = draft.trim();
    if (trim === '') { onChange(null); return; }
    const n = Number(trim);
    if (!Number.isFinite(n)) { setDraft(value == null ? '' : String(value)); return; }
    onChange(n);
  };

  const seeded = value != null;
  return (
    <div
      className={`inline-flex items-center gap-1.5 ${seeded ? 'bg-brand-soft text-brand border-brand/30' : 'bg-surface-2 text-ink-2 border-line'}`}
      style={{ height: 30, padding: '0 8px', border: '1px solid', borderRadius: 'var(--radius-md)' }}
      title="Seed für reproduzierbare Monte-Carlo-Läufe. Leer = jeder Lauf zufällig, Zahl = deterministisch."
    >
      <Hash size={11} className={seeded ? 'text-brand' : 'text-ink-3'} aria-hidden="true" />
      <span className={`text-[10.5px] font-bold tracking-wider uppercase ${seeded ? 'text-brand' : 'text-ink-3'}`}>
        Seed
      </span>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); } }}
        inputMode="numeric"
        placeholder="zufall"
        aria-label="Monte-Carlo Seed (leer = zufällig)"
        className="num"
        style={{
          width: 64, height: 20, padding: 0,
          background: 'transparent', border: 'none', outline: 'none',
          fontSize: 12, fontWeight: 600,
          color: seeded ? 'var(--color-brand)' : 'var(--color-ink)',
        }}
      />
    </div>
  );
}

// ─── SubBar ────────────────────────────────────────────────────────

const selectSubBarState = (s) => ({
  mode: s.mode,
  setMode: s.setMode,
  mcSamples: s.mcSamples,
  setMcSamples: s.setMcSamples,
  mcSeed: s.mcSeed,
  setMcSeed: s.setMcSeed,
  liveCalc: s.liveCalc,
  setLiveCalc: s.setLiveCalc,
  isDirty: s.isDirty,
  computeNow: s.computeNow,
  counted: s.counted,
  startCalculation: s.startCalculation,
  elementsCount: s.elements.length,
});

export default function SubBar() {
  const {
    mode, setMode,
    mcSamples, setMcSamples,
    mcSeed, setMcSeed,
    liveCalc, setLiveCalc,
    isDirty, computeNow,
    counted, startCalculation,
    elementsCount,
  } = useToleranceStore(useShallow(selectSubBarState));
  const isPro = useAuthStore(selectIsPro);
  const isFree = useAuthStore(selectIsFree);
  const planStatus = useAuthStore(useShallow(selectPlanStatus));
  const incrementCalculation = useAuthStore((s) => s.incrementCalculation);

  // ── Free-tier BERECHNEN flow ─────────────────────────────────
  // Free users explicitly trigger their once-per-month budget hit by
  // clicking BERECHNEN. Until they do, the store's calculate() no-ops
  // and the ResultsPanel stays at the empty state.
  //
  // The confirmation modal that used to interrupt this flow was
  // removed per user feedback: with the new 25-calcs/month budget the
  // friction wasn't worth it, and the action is fully reversible
  // (clicking burns 1 credit but the chain stays editable + the
  // persistent counter chip remains visible). Replaced by a toast
  // that surfaces the consumption after the fact.
  const [incrementing, setIncrementing] = useState(false);
  const handleFreeBerechnen = async () => {
    if (incrementing) return; // guard against double-click
    setIncrementing(true);
    try {
      let used;
      if (isFree && !counted) {
        const data = await incrementCalculation();
        used = data?.used;
      }
      startCalculation();
      trackEvent('Calculate Clicked', { plan: isFree ? 'free' : 'pro' });
      const usedNow = used ?? planStatus.used + (isFree && !counted ? 1 : 0);
      toast.success(`Berechnung gestartet · ${usedNow}/${planStatus.total} diesen Monat`);
    } catch (err) {
      toast.error(err.message || 'Fehler beim Starten der Berechnung');
    } finally {
      setIncrementing(false);
    }
  };

  // Live-Calc gating: Free users can't pause computation (it's tied
  // to the BERECHNEN flow that consumes their monthly budget — see
  // mobile-policy memo). We keep the switch visible but disabled so
  // they understand the affordance exists in Pro.
  const liveDisabled = !isPro;
  const showSamplesWarning = mode === 'montecarlo' && mcSamples >= SAMPLES_WARN_THRESHOLD && liveCalc;
  const showFreeBerechnen = isFree && !counted && elementsCount > 0;

  return (
    <div
      className="bg-surface border-b border-line flex items-center px-4 gap-3 shrink-0"
      style={{ height: 'var(--subbar-h)' }}
    >
      {/* „Berechnungsmodus"-Label kostet ~140 px und ist nur als
          Kontext für die Tabs daneben da — die Method-Icons + Labels
          erklären sich von selbst. Ab xl (1280 px) wieder sichtbar
          für die volle Optik. */}
      <span className="hidden 2xl:inline text-[10.5px] font-bold tracking-wider uppercase text-ink-3">
        Berechnungsmodus
      </span>
      <MethodTabs value={mode} onChange={setMode} isFree={isFree} />
      <WissenHinweis id="methoden" />

      {mode === 'montecarlo' && (
        <>
          <span aria-hidden="true" className="bg-line" style={{ width: 1, height: 22 }} />
          <SamplesPicker
            value={mcSamples}
            onChange={setMcSamples}
            warn={showSamplesWarning}
            onDisableLive={() => setLiveCalc(false)}
          />
          <SeedPicker value={mcSeed} onChange={setMcSeed} />
        </>
      )}

      <div className="flex-1" />

      {showFreeBerechnen && (
        <button
          type="button"
          onClick={handleFreeBerechnen}
          disabled={!planStatus.canCalculate || incrementing}
          title={planStatus.canCalculate
            ? 'Berechnung starten'
            : 'Monatskontingent erreicht - Upgrade auf Pro'}
          className="inline-flex items-center gap-1.5 text-xs font-semibold bg-ok text-white border-ok shadow-md disabled:opacity-50"
          style={{ height: 30, padding: '0 14px', border: '1px solid', borderRadius: 'var(--radius-md)', cursor: planStatus.canCalculate ? 'pointer' : 'not-allowed' }}
        >
          <Calculator size={12} aria-hidden="true" />
          BERECHNEN
          <span className="num bg-white/20 text-white" style={{ padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 700, marginLeft: 4 }}>
            {planStatus.used} / {planStatus.total}
          </span>
        </button>
      )}

      {/* Persistent counter chip — only after the Free user has clicked
          BERECHNEN (counted=true). Replaces the now-hidden BERECHNEN
          button so the user keeps seeing how many monthly calcs they
          have left. Tinted warn/err as the budget runs out. */}
      {isFree && counted && (
        <span
          title={planStatus.remaining > 0
            ? `${planStatus.remaining} Berechnung${planStatus.remaining === 1 ? '' : 'en'} diesen Monat übrig`
            : 'Monatskontingent erschöpft - Upgrade auf Pro für unbegrenzte Berechnungen'}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${
            planStatus.remaining === 0
              ? 'bg-err/15 text-err border-err/40'
              : planStatus.remaining <= 1
                ? 'bg-warn/15 text-warn border-warn/40'
                : 'bg-surface-2 text-ink-2 border-line'
          }`}
          style={{ height: 30, padding: '0 10px', border: '1px solid', borderRadius: 'var(--radius-md)' }}
        >
          <Calculator size={11} aria-hidden="true" />
          <span className="num">{planStatus.used} / {planStatus.total}</span>
          <span>Berechnungen</span>
        </span>
      )}

      <ThermalPicker />

      <LiveCalcSwitch value={liveCalc} onChange={setLiveCalc} disabled={liveDisabled} />
      {!liveCalc && (
        <button
          type="button"
          onClick={computeNow}
          title="Snapshot mit aktuellen Eingaben neu berechnen"
          className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${
            isDirty
              ? 'bg-brand text-white border-brand shadow-md'
              : 'bg-surface-2 text-ink-3 border-line'
          }`}
          style={{ height: 30, padding: '0 14px', border: '1px solid', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
        >
          {isDirty ? <Play size={12} aria-hidden="true" /> : <Check size={12} aria-hidden="true" />}
          {isDirty ? 'Berechnen' : 'Aktuell'}
        </button>
      )}

      <span aria-hidden="true" className="bg-line" style={{ width: 1, height: 22 }} />

      <div className="flex items-center gap-2.5 text-xs text-ink-3">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="num bg-brand-soft text-brand inline-flex items-center justify-center text-[11px] font-bold"
            style={{ width: 18, height: 18, borderRadius: 4 }}
          >
            {elementsCount}
          </span>
          {/* „Maße"-Wort blendet sich unter xl (1280 px) aus — Zahl in
              Brand-Color reicht zur Identifikation. Spart ~32 px. */}
          <span className="hidden 2xl:inline">Maße</span>
        </span>
        {/* mm-Einheits-Badge ist redundant in dem Sinne, dass die App
            durchgehend in mm rechnet; bei eng versteckt, ab xl sichtbar. */}
        <span
          className="hidden 2xl:inline-flex bg-surface-2 border border-line text-ink-2 font-semibold text-[11.5px]"
          style={{ padding: '4px 8px', borderRadius: 6 }}
        >
          mm
        </span>
      </div>

    </div>
  );
}
